import axios from '../lib/axios';
import { shuffle as shuffleFunc } from '../lib/shuffle';
import { decorateExercise } from '../lib/decorate-exercise';
import { CHECKER_URL } from '../config';

export const GET_HOMEWORK_START = 'GET_HOMEWORK_START';
export const GET_HOMEWORK_SUCCESS = 'GET_HOMEWORK_SUCCESS';
export const GET_HOMEWORK_ERROR = 'GET_HOMEWORK_ERROR';

const generateHomeworkUrl = (originalId, isPreview, saveStatus, version, bookId) => {
    if (isPreview) {
        return `/api/homework/${originalId}/preview`;
    }

    let url = '/api';

    if (!saveStatus) {
        url += '/open';
    }

    url += `/homework/${originalId}`;

    if (version) {
        url += `/version/${version}`;
    }

    if (bookId) {
        url += `?bookId=${bookId}`;
    }

    return url;
};

async function decorateHomework(profile, status, homework, isSolution) {
    const { shuffle = false } = profile;
    const { randomSeed, sections } = status;
    let exercises = homework.exercises;
    if (!homework.avoidShuffle && shuffle) {
        exercises = shuffleFunc(exercises, randomSeed);
    }

    const decoratedExercises = [];
    for (let i = 0; i < exercises.length; i++) {
        decoratedExercises.push(await decorateExercise(exercises[i], randomSeed, isSolution));
    }
    const decoratedHomework = {
        ...homework,
        exercises: decoratedExercises,
    };
    return { sections, decoratedHomework };
}

export function setHomework(homework, status, profile, isSolution = false) {
    return async (dispatch) => {
        const { sections, decoratedHomework } = await decorateHomework(profile, status, homework, isSolution);

        dispatch({
            type: GET_HOMEWORK_SUCCESS,
            payload: {
                homework: decoratedHomework,
                source: profile.source,
                sections: sections,
            },
        });
        return decoratedHomework;
    };
}

export function getHomework(profile, status, isSolution = false) {
    return async (dispatch) => {
        const { homeworkVersion } = status;
        const { originalId, isPreview = false, saveStatus = false, bookId } = profile;

        try {
            if (originalId) {
                dispatch({ type: GET_HOMEWORK_START });
                const url = generateHomeworkUrl(originalId, isPreview, saveStatus, homeworkVersion, bookId);
                const response = await axios.get(url);
                const homework = response.data;
                const { sections, decoratedHomework } = await decorateHomework(profile, status, homework, isSolution);

                dispatch({
                    type: GET_HOMEWORK_SUCCESS,
                    payload: {
                        homework: decoratedHomework,
                        source: profile.source,
                        sections: sections,
                    },
                });

                return decoratedHomework;
            }
        } catch (e) {
            dispatch({
                type: GET_HOMEWORK_ERROR,
                payload: {
                    error: e.response?.status || 500,
                },
            });
        }
    };
}

export const GET_EXERCISE_SUCCESS = 'GET_EXERCISE_SUCCESS';
export const GET_EXERCISE_START = 'GET_EXERCISE_START';
export const GET_EXERCISE_ERROR = 'GET_EXERCISE_ERROR';

export function getExerciseById(id) {
    return async (dispatch) => {
        dispatch({ type: GET_EXERCISE_START });

        try {
            const url = `/api/exercises/${id}/preview`;
            const { data } = await axios.get(url);

            let random = Math.random();
            const exercise = await decorateExercise(data, random);

            dispatch({
                type: GET_EXERCISE_SUCCESS,
                payload: {
                    exercise,
                    random,
                },
            });
            return true;
        } catch (e) {
            dispatch({
                type: GET_EXERCISE_ERROR,
                payload: {
                    error: e.response?.status || 500,
                },
            });
            return false;
        }
    };
}

export function getTrymeById(id) {
    return async (dispatch) => {
        dispatch({ type: GET_EXERCISE_START });

        try {
            const url = `/api/tryme/${id}`;
            const { data } = await axios.get(url);

            let random = Math.random();
            const exercise = await decorateExercise(data, random);

            dispatch({
                type: GET_EXERCISE_SUCCESS,
                payload: {
                    exercise,
                    random,
                },
            });
            return true;
        } catch (e) {
            dispatch({
                type: GET_EXERCISE_ERROR,
                payload: {
                    error: e.response?.status || 500,
                },
            });
            return false;
        }
    };
}

export const GET_THEORIES_START = 'GET_THEORIES_START';
export const GET_THEORIES_SUCCESS = 'GET_THEORIES_SUCCESS';
export const GET_THEORIES_ERROR = 'GET_THEORIES_ERROR';

export function getTheoriesByExerciseId(exerciseId, bookId) {
    return async (dispatch) => {
        dispatch({
            type: GET_THEORIES_START,
        });
        try {
            const url = `/api/theories/${exerciseId}/${bookId}`;
            const { data } = await axios.get(url);
            dispatch({
                type: GET_THEORIES_SUCCESS,
                payload: {
                    id: exerciseId,
                    data: data ? data : [],
                },
            });
        } catch (e) {
            dispatch({
                type: GET_THEORIES_ERROR,
                payload: {
                    id: exerciseId,
                    error: e.message,
                },
            });
        }
    };
}

export const UPDATE_GEOGEBRA = 'UPDATE_GEOGEBRA';
export const RESET_GEOGEBRA = 'RESET_GEOGEBRA';

export function updateGeogebra(id, geogebra, image) {
    return {
        type: geogebra ? UPDATE_GEOGEBRA : RESET_GEOGEBRA,
        payload: { id, geogebra, image },
    };
}

export const VALIDATE_CHECKER_STEP_START = 'VALIDATE_CHECKER_STEP_START';
export const VALIDATE_CHECKER_STEP_SUCCESS = 'VALIDATE_CHECKER_STEP_SUCCESS';
export const VALIDATE_CHECKER_STEP_ERROR = 'VALIDATE_CHECKER_STEP_ERROR';

export const VALIDATE_CHECKER_RESULT_START = 'VALIDATE_CHECKER_RESULT_START';
export const VALIDATE_CHECKER_RESULT_SUCCESS = 'VALIDATE_CHECKER_RESULT_SUCCESS';
export const VALIDATE_CHECKER_RESULT_ERROR = 'VALIDATE_CHECKER_RESULT_ERROR';

export function validateCheckerStep(assertionType, checkFirst, checkPrevious, listSteps, correctResult, type, originalId) {
    return async (dispatch) => {
        dispatch({
            type: type === 'step' ? VALIDATE_CHECKER_STEP_START : VALIDATE_CHECKER_RESULT_START,
            payload: {
                id: originalId,
            },
        });
        try {
            const { data } = await axios.post(CHECKER_URL, {
                assertion_type: assertionType,
                check_first: checkFirst,
                check_previous: checkPrevious,
                list_steps: listSteps,
                correct_result: correctResult,
                button: type,
            });
            return dispatch({
                type: type === 'step' ? VALIDATE_CHECKER_STEP_SUCCESS : VALIDATE_CHECKER_RESULT_SUCCESS,
                payload: { id: originalId, data: data.checker_results },
            });
        } catch (e) {
            dispatch({
                type: type === 'step' ? VALIDATE_CHECKER_STEP_ERROR : VALIDATE_CHECKER_RESULT_ERROR,
                payload: {
                    id: originalId,
                    error: e.message,
                },
            });
        }
    };
}
